import * as React from 'react'

import { Temporal } from '@js-temporal/polyfill'
import { clsx } from 'clsx'

import {
  CloseIcon,
  DropdownMenu,
  Flex,
  IconButton,
  type DropdownMenuProps,
} from '@buffer-mono/popcorn'

import { formatTimestamp as formatTimestampWithTimezone } from '~publish/helpers/temporal'
import { useChannelSlots } from '~publish/hooks/useChannelSlots'
import { useTimezone } from '~publish/hooks/useTimezone'
import { useWeekStartsOn } from '~publish/hooks/useWeekStartsOn'
import { useTwentyFourHourTimeFormat } from '~publish/pages/Calendar/hooks/useTwentyFourHourTimeFormat'

import { ScheduleDatePicker } from './ScheduleDatePicker'
import { ScheduleSelectorCoachmark } from './ScheduleSelectorCoachmark'
import { ScheduleSelectorOptions } from './ScheduleSelectorOptions'
import { ScheduleOptionTrigger } from './ScheduleSelectorTrigger'
import type { ScheduleOption, ScheduleSelectorState } from './types'
import { useSetDraftsScheduledAt } from '../useSetDraftsScheduledAt'

import styles from './ScheduleSelector.module.css'

interface ScheduleSelectorProps extends DropdownMenuProps {
  value: ScheduleSelectorState
  onChange: (args: ScheduleSelectorState) => void
  options: ScheduleOption[]
  className?: string
  timezone?: string
  initialScheduledAt?: number | null
  defaultOption?: ScheduleOption
  fallbackOption: ScheduleOption
  channelIdForSlots?: string
}

const DEFAULT_PICKER_OFFSET_MINUTES = 60

export const ScheduleSelector: React.FC<ScheduleSelectorProps> = ({
  className,
  onChange,
  value,
  options,
  timezone: timezoneFromProfiles,
  defaultOption,
  fallbackOption,
  channelIdForSlots,
  ...props
}) => {
  const userTimezone = useTimezone()
  const timezone = timezoneFromProfiles ?? userTimezone
  const twentyFourHourTimeFormat = useTwentyFourHourTimeFormat()
  const weekStartsMonday = useWeekStartsOn() === 'monday'
  const [open, setOpen] = React.useState(false)

  const { setDraftsScheduledAt } = useSetDraftsScheduledAt()

  const defaultDatePickerValue = React.useMemo(
    () =>
      Temporal.Now.zonedDateTimeISO(timezone).add({
        minutes: DEFAULT_PICKER_OFFSET_MINUTES,
      }),
    [timezone],
  )

  const [datePickerValue, setDatePickerValue] = React.useState<
    Temporal.ZonedDateTime | undefined
  >(undefined)

  React.useEffect(() => {
    if (value.scheduledAt) {
      setDatePickerValue(
        Temporal.Instant.fromEpochSeconds(value.scheduledAt).toZonedDateTimeISO(
          timezone,
        ),
      )
    }
  }, [value.scheduledAt, timezone])

  const { slots, loading: slotsLoading } = useChannelSlots({
    channelId: channelIdForSlots,
    dateTime: datePickerValue ?? defaultDatePickerValue,
  })

  const showDatePicker = value.option === 'FixedTime'

  const formatTimestamp = (timestamp: number): string =>
    formatTimestampWithTimezone(timestamp, {
      timezone,
      twentyFourHourTimeFormat,
    })

  const handleOptionChange = ({
    option,
    keepOpen = false,
  }: {
    option: ScheduleOption
    keepOpen?: boolean
  }): void => {
    setDraftsScheduledAt(null)
    onChange({
      option,
      scheduledAt: null,
    })
    if (option !== 'FixedTime') {
      // keep modal open for the date picker
      setOpen(keepOpen)
    }
  }

  const onSelect = (option: ScheduleOption, event: Event): void => {
    if (option === 'FixedTime') {
      event.preventDefault() // Keep dropdown open
    }
    handleOptionChange({ option })
  }

  const handleOpenChange = (newOpenState: boolean): void => {
    if (open !== newOpenState) {
      setOpen(newOpenState)
    }
  }

  const onRemoveFixedDate = (): void => {
    handleOptionChange({ option: fallbackOption, keepOpen: false })
  }

  const onResetDatePicker = (): void => {
    handleOptionChange({ option: fallbackOption, keepOpen: true })
  }

  const handleDateChange = React.useCallback(
    (zonedDateTime: Temporal.ZonedDateTime | undefined): void => {
      if (!slots && datePickerValue === undefined) {
        // we update the default time to the first slot available, but we don't want to
        // propagate the change in case the user just closes the date picker without selecting a date
        setDatePickerValue(zonedDateTime)
      } else {
        const newValue = zonedDateTime?.epochSeconds ?? null
        setDraftsScheduledAt(newValue)
        onChange({
          option: 'FixedTime',
          scheduledAt: newValue,
        })
      }
    },
    [onChange, setDraftsScheduledAt],
  )

  const handleDateSubmit = (zonedDateTime: Temporal.ZonedDateTime): void => {
    handleDateChange(zonedDateTime)
    setOpen(false)
  }

  const dropDownContentClassName = showDatePicker
    ? styles.scheduleSelectorDatePicker
    : styles.scheduleSelectorContent

  const handleEscapeKeyDown = (e: KeyboardEvent): void => {
    // Manually handle Escape key press when the dropdown is open.
    // Although Popcorn/Radix DropdownMenu typically handles Escape,
    // the legacy composer structure and its own Escape key listeners
    // require explicit prevention of default behavior and stopping
    // event propagation here to prevent the main composer from closing.
    if (open) {
      e.preventDefault()
      e.stopPropagation()
      // Ensure we also update the controlled open state
      handleOpenChange(false)
    }
  }

  const showRemoveFixedDate =
    value.option === 'FixedTime' && value.scheduledAt !== null

  return (
    <Flex className={className} direction="row" gap="2xs" align="center">
      <DropdownMenu open={open} onOpenChange={handleOpenChange} {...props}>
        <DropdownMenu.Trigger asChild>
          <ScheduleOptionTrigger
            currentSelection={value}
            formatTimestamp={formatTimestamp}
          />
        </DropdownMenu.Trigger>
        {showRemoveFixedDate && (
          <IconButton
            className={styles.scheduleSelectorRemoveFixedDate}
            label="Remove fixed date"
            variant="secondary"
            size="small"
            onClick={onRemoveFixedDate}
          >
            <CloseIcon />
          </IconButton>
        )}
        <DropdownMenu.Content
          align="start"
          side="top"
          className={clsx(
            dropDownContentClassName,
            showDatePicker && styles.scheduleSelectorNoPadding,
          )}
          onEscapeKeyDown={handleEscapeKeyDown}
          onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>): void => {
            if (showDatePicker && e.key === 'Tab') {
              // This is a workaround to allow keyboard navigation to the date picker when it's shown.
              // Radix DropdownMenu prevents default behavior for the Tab key, since it
              // uses roving tabindex to manage focus movement among menu items.
              // https://www.radix-ui.com/primitives/docs/components/dropdown-menu#accessibility
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error - Workaround to allow keyboard navigation to the date picker
              e.nativeEvent.defaultPrevented = false
            }
          }}
        >
          {showDatePicker ? (
            <ScheduleDatePicker
              value={datePickerValue}
              defaultValue={defaultDatePickerValue}
              onChange={handleDateChange}
              onSubmit={handleDateSubmit}
              onReset={onResetDatePicker}
              timezone={timezone}
              weekStartsOn={weekStartsMonday ? 1 : 0}
              hourCycle={twentyFourHourTimeFormat ? 24 : 12}
              slots={slots ?? []}
              slotsLoading={slotsLoading}
            />
          ) : (
            <ScheduleSelectorOptions
              onSelect={onSelect}
              currentSelection={value}
              options={options}
              defaultOption={defaultOption}
            />
          )}
        </DropdownMenu.Content>
      </DropdownMenu>
      <ScheduleSelectorCoachmark />
    </Flex>
  )
}
